import React, { useContext } from 'react'
import { DateTime } from 'luxon'
import Collapsible from './collapsible'
import Wizard from './wizard'
import { components, defaults, DisabledContext, EditModeContext, NamePrefixContext, ErrorPathContext, SchemaContext, combineProps } from './detailsPageComponents'

export default function DetailsArea({ fields, className }) {
  const schema = useContext(SchemaContext)
  const namePrefix = useContext(NamePrefixContext)
  const errorPath = useContext(ErrorPathContext)
  const editMode = useContext(EditModeContext)
  const parentDisabled = useContext(DisabledContext)

  const { TextInput, NumberInput, TextArea, CheckBox, RadioGroup, Dropdown, Date: DateInput, DateTime: DateTimeInput, FileInput, Button, ErrorIcon, ErrorText, Text, Table, DisplayField, DefaultListContainer, DefaultListItem, ...registeredComponents } = components
  function fieldName(field) {
    if (namePrefix) return `${namePrefix}.${field.name}`
    else return field.name
  }
  function getChildNames(field) {
    let names = [fieldName(field)]
    if (Array.isArray(field.fields)) {
      for (const fld of field.fields) names = names.concat(getChildNames(fld))
    }
    return names
  }
  function fieldValue(field, name, defaultValue = '') {
    if (typeof field.value === 'function') return field.value({ get: schema.get }) ?? defaultValue
    return schema.get(name) ?? defaultValue
  }

  function toBool(value) {
    if (value === 'false') return false
    return Boolean(value)
  }

  function getInput(field, value, path) {
    let disabled = false
    let disableChildren = false
    if (field.disabled) {
      disabled = true
      disableChildren = true
    }
    if (parentDisabled && !field.ignoreParentDisabled) {
      disabled = true
      disableChildren = true
    }
    if (!editMode && field.editOnly !== false) disabled = true

    const name = path !== undefined ? path : fieldName(field)
    const { defaultValue, ...componentDefaults } = defaults[(typeof field.component === 'function') ? field.component.name : field.component] ?? {}
    const componentProps = combineProps(field.componentProps, componentDefaults)
    const fieldProps = {
      ...componentProps,
      id: `details-item-${name}`,
      name,
      label: field.label,
      className: field.className,
      placeholder: field.placeholder,
      fields: field.fields,
      value: value !== undefined ? value : fieldValue(field, name, 'defaultValue' in field ? field.defaultValue : defaultValue),
      onChange: field.onChange
        ? field.onChange
        : (field.afterChange
          ? async (event) => {
            const change = await schema.onChange(event)
            field.afterChange(change) // change => { [name]: value }
          }
          : schema.onChange),
      // onBlur: () => schema.validate(name),
      disabled
    }

    if (typeof field.component === 'function') {
      const Type = field.component
      return <Type {...fieldProps} />
    } else if (field.component === 'fieldSet' || field.component === 'collapsible') {
      return (
        <ErrorPathContext.Provider value={name}>
          <DisabledContext.Provider value={disableChildren}>
            <DetailsArea
              {...componentDefaults}
              fields={field.fields}
              className={fieldProps.className}
            />
          </DisabledContext.Provider>
        </ErrorPathContext.Provider>
      )
    } else if (field.component === 'textInput') return <TextInput type='text' {...fieldProps} />
    else if (field.component === 'text') return <Text {...fieldProps}>{fieldProps.value}</Text>
    else if (field.component === 'displayField') return <DisplayField {...fieldProps}/>
    else if (field.component === 'numberInput') return <NumberInput type='number' {...fieldProps} />
    else if (field.component === 'textArea') return <TextArea {...fieldProps} />
    else if (field.component === 'checkBox') return <CheckBox {...fieldProps} type='checkbox' value={fieldProps.name} checked={toBool(fieldProps.value)} />
    else if (field.component === 'radioGroup') return <RadioGroup {...fieldProps} options={field.options} />
    else if (field.component === 'date') return <DateInput {...fieldProps} type='date' value={fieldProps.value ? DateTime.fromISO(fieldProps.value).toISODate() : fieldProps.value} />
    // else if (field.component === 'dateTime') return <input {...fieldProps} type='datetime-local' value={fieldProps.value ? DateTime.fromISO(fieldProps.value).toISO().slice(0, 16) : fieldProps.value} />
    else if (field.component === 'dateTime') return <DateTimeInput {...fieldProps} />
    else if (field.component === 'file') {
      if (fieldProps.readFormat == null && schema.getCurrentSchemaValue('readFormat', name)) fieldProps.readFormat = schema.getCurrentSchemaValue('readFormat', name)
      fieldProps.file = fieldProps.value
      delete fieldProps.value
      return <FileInput {...fieldProps} type='file' />
    } else if (field.component === 'button') return <Button {...fieldProps} onClick={field.onClick}>{field.buttonCaption}</Button>
    else if (field.component === 'dropdown') {
      return (
        <Dropdown
          {...fieldProps}
          options={field.options}
        />
      )
    } else if (field.component === 'list') {
      const ListRenderer = field.listComponent || DefaultListContainer
      const ItemRenderer = field.itemComponent || DefaultListItem || DetailsArea
      const dataArray = Array.isArray(fieldProps.value) ? fieldProps.value : []
      // Overwrite priority componentProps > specific list defaults > list defaults
      const componentProps = combineProps(field.componentProps, defaults[ListRenderer.componentKey], componentDefaults)
      return <ListRenderer
        ItemRenderer={ItemRenderer}
        {...fieldProps}
        {...componentProps}
        value={dataArray}
        newItem={field.newItem}
        canDelete={field.canDelete}
        titleFunction={field.titleFunction}
        getErrorClass={getErrorClass}
      />
    } else if (field.component === 'table') {
      return <Table
        {...fieldProps}
        columns={field.fields.map(({ label: Header, ...fld }) => ({
          accessor: fld.name,
          ...fld,
          Header,
          ...(fld.component === 'tableDefault' ? {} : { Cell: (cell) => getInput(fld, cell.value, `${name}.${cell.row.index ?? cell.row._index ?? cell.index}.${fld.name}`) })
        }))}
        data={(Array.isArray(fieldProps.value) ? fieldProps.value : []).map(row => field.fields.reduce((obj, fld) => Object.assign(obj, { [fld.name]: row[fld.name] }), {}))}
        {...field.tableProps}
      />
    } else if (field.component === 'wizard') {
      return <Wizard
        {...fieldProps}
        containerClass='schema-form__wizard-container'
        bottomNavButtons={field.bottomNavButtons ?? false}
        pages={field.pages.map(page => {
          const childNames = getChildNames(page)
          return {
            title: schema.hasChildError(childNames) ? <span>{page.title}&nbsp;<ErrorIcon className={getErrorClass(schema.getChildErrors(childNames)?.flat())}/></span> : page.title,
            hidden: page.hidden,
            component: (
              <ErrorPathContext.Provider value={name}>
                <DisabledContext.Provider value={disableChildren}>
                  <DetailsArea fields={page.fields} className={page.className || 'borderless'} />
                </DisabledContext.Provider>
              </ErrorPathContext.Provider>
            ),
            navDisabled: page.navDisabled
          }
        })}
      />
    } else if (registeredComponents[field.component]) {
      const CustomComp = registeredComponents[field.component]
      return <CustomComp {...fieldProps} />
    } else return `Component not yet implemented: ${field.component}`
  }

  function getErrorClass(err) {
    const errs = Array.isArray(err) ? err : [err]
    let cls = 'schema-form__details-validation_error'
    for (const oneErr of errs) {
      if (oneErr.type) {
        if (!cls.includes(`type--${oneErr.type}`)) cls += ` type--${oneErr.type}`
      } else cls += ' type--error'
    }
    // console.log('getErrorClass', err, cls)
    return cls
  }
  // An error must have a unique combination of [path, message, type]
  // Return an array of errs with the first unqiue instance found possessing the combination above in the provided err array
  function filterDuplicateErrors(err) {
    const errMap = new Map()
    for (const error of (Array.isArray(err) ? err : [err])) { errMap.set(`${error.path.toString()}||${error.message}||${error.type}`, error) }
    return [...errMap.values()]
  }
  function getFieldError(field) {
    const err = schema.getError(fieldName(field))
    // if (err) console.log('getError', fieldName(field), field, err)
    if (!err) return null
    else if (Array.isArray(err)) return filterDuplicateErrors(err).map((e, i) => <ErrorText key={i} className={getErrorClass(e)}>{e.message}</ErrorText>)
    else return <ErrorText className={getErrorClass(err)}>{err.message}</ErrorText>
  }
  function getFieldSetError() {
    const err = schema.getError(errorPath)
    // console.log('getFieldSetError', errorPath, err)
    if (!err) return null
    else if (Array.isArray(err)) return filterDuplicateErrors(err).map((e, i) => <ErrorText key={i} className={getErrorClass(e)}>{e.message}</ErrorText>)
    else return <ErrorText className={getErrorClass(err)}>{err.message}</ErrorText>
  }

  if (!fields?.length) return null
  return <div className={`schema-form__details-area ${className ?? ''}`}>
    {fields
      .filter(field => !field.hidden)
      .map(field => {
        if (field.component === 'collapsible') {
          const childNames = getChildNames(field)
          return (
            <Collapsible
              key={field.id}
              containerClass='collapsible-container'
              contentClass='schema-form__details-area borderless'
              header={<span>
                <label htmlFor={`details-item-${fieldName(field)}`}>{field.label}</label>
                {schema.hasChildError(childNames) ? <>&nbsp;<ErrorIcon className={getErrorClass(schema.getChildErrors(childNames)?.flat())}/></> : null}
              </span>}
              noWrapper={true}
            >
              {(className) => <>
                {getInput({ ...field, className })}
                {getFieldError(field)}
              </>}
            </Collapsible>
          )
        } else {
          const componentClassName = typeof field.component === 'string' ? field.component : field.component.name
          const classNames = ['']
          if (field.span) classNames.push('span')
          if (field.vSpan) classNames.push(`v-span-${field.vSpan}`)
          if (field.split) classNames.push(`split${(!isNaN(parseFloat(field.split)) && Number.isInteger(Number(field.split))) ? `-${field.split}` : ''}`)
          if (field.containerClassName) classNames.push(field.containerClassName)
          return (
            <div key={fieldName(field)} className={`schema-form__details-field schema-form__component-${componentClassName}${classNames.join(' ')}`}>
              <label className={`schema-form__details-field__default-label schema-form__component-${componentClassName}`} htmlFor={`details-item-${fieldName(field)}`}>{field.label}</label>
              {getInput(field)}
              {getFieldError(field)}
            </div>
          )
        }
      })}
    {getFieldSetError()}
  </div>
}
